import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SlipContext } from 'context/slipContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { getEventByid } from 'services/api/sportBetsService';
import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { Odd } from 'types/Odd';
import { Slip } from 'types/Slip';
import {
  canReplayRemaining,
  findPickInOffer,
  remainingMatches,
} from 'utils/replayTicket';
import { usePlayerLayout } from 'lib/playerLayout';
import { openSlipSheet } from 'lib/slipSheet';
import styles from './ReplayRemaining.module.css';

interface Props {
  slip: Slip;
}

/**
 * "Play the rest" / "Continue with this ticket": puts the picks of a ticket's
 * matches that have not started yet on the slip, at today's prices, for a new
 * ticket.
 */
export const ReplayRemaining = ({ slip }: Props) => {
  const { t } = useTranslation();
  const { addPrematchSlipEntries } = useContext(SlipContext);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const modern = usePlayerLayout() === 'modern';
  const [busy, setBusy] = useState(false);

  if (!canReplayRemaining(slip)) return null;

  const replay = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const matches = remainingMatches(slip);
    const picks: { match: MatchOffer; bet: Bet; odd: Odd }[] = [];
    let missing = 0;
    const offers = await Promise.all(
      matches.map((x) => getEventByid(Number(x.event.eventId)))
    );
    matches.forEach((x, i) => {
      const match = offers[i] as MatchOffer | null | undefined;
      const open = match && match.time * 1000 > Date.now();
      x.bets.forEach((bet) => {
        const found = open ? findPickInOffer(match, bet) : null;
        if (found) {
          picks.push({ match: match as MatchOffer, ...found });
        } else {
          missing++;
        }
      });
    });
    setBusy(false);

    if (picks.length === 0) {
      toast.error(t('replay_remaining_none_label'));
      return;
    }
    addPrematchSlipEntries(picks);
    if (missing > 0) {
      toast.info(t('replay_remaining_some_missing_label', { count: missing }));
    }
    if (isMobile && modern) openSlipSheet();
    else navigate(isMobile ? '/bet-slip' : '/sports-betting');
  };

  return (
    <button
      type="button"
      className={styles.button}
      onClick={replay}
      disabled={busy}
    >
      {t(
        slip.awaiting_other_matches
          ? 'replay_remaining_label'
          : 'replay_ticket_label'
      )}
    </button>
  );
};
